'use client';

import { c as _c } from "react/compiler-runtime";
import { Badge } from '@common/components';
import { INTERACTIVE_SURFACE } from '@common/components/interaction';
import SvgChevronright from '@common/icon/SvgChevronright';
import SvgPointStore from '@common/icon/SvgPointStore';
import { cn } from '@core/utils';
import type { ReactNode } from 'react';
import { CustomLink } from '@/app/_components/CustomLink';
import { PointCoin } from '@/app/_components/PointCoin';
import { Image } from '@/components/client';
import { ROUTES } from '@/constants';
import { AFFILIATE_DISCLOSURE, CHANNEL_LABEL, MERCHANT_SECTION_LABEL } from '../_constants';
import { type AffiliateMerchant, useAffiliateMerchants } from '../_hooks/useAffiliateMerchants';
import { resolveRewardRateLabel } from '../_utils/formatRewardRate';
import { groupMerchantsBySection, type MerchantSectionType } from '../_utils/groupMerchantsBySection';
import { MerchantListEmpty } from './MerchantListEmpty';
const POINT_STORE_ICON = <SvgPointStore originalFill className="size-24" />;
const SECTION_HEADER = {
  LARGE_SHOPPING: {
    label: MERCHANT_SECTION_LABEL.LARGE_SHOPPING,
    icon: POINT_STORE_ICON
  },
  TRAVEL_STAY: {
    label: MERCHANT_SECTION_LABEL.TRAVEL_STAY,
    icon: <Image src="/assets/pngs/benefit-merchants/travel-carrier.png" alt="" width={24} height={24} />
  },
  ETC: {
    label: MERCHANT_SECTION_LABEL.ETC,
    icon: POINT_STORE_ICON
  }
} satisfies Record<MerchantSectionType, {
  label: string;
  icon: ReactNode;
}>;
const CHANNEL_BADGE_CLASS_NAME = {
  ALL: null,
  WEB_ONLY: 'bg-M-25 text-M-400',
  APP_ONLY: 'bg-B-25 text-B-500'
} as const;
const isInstantPayout = (merchant: AffiliateMerchant) => merchant.payoutType === 'BATCH' && merchant.rewardCurrencyType === 'WELLO_POINT';
const InstantPayoutBadge = t0 => {
  const $ = _c(5);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("w-fit gap-2 bg-Y-50 font-700 text-Y-600", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <PointCoin currency="WELLO_POINT" size={12} />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <Badge dimensions="H20" className={t1}>{t2}웰포인트 즉시 지급</Badge>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
interface MerchantRowProps {
  merchant: AffiliateMerchant;
  isPayoutBadgeVisible: boolean;
}
const MerchantRow = t0 => {
  const $ = _c(37);
  const {
    merchant,
    isPayoutBadgeVisible
  } = t0;
  let t1;
  if ($[0] !== merchant) {
    t1 = resolveRewardRateLabel(merchant);
    $[0] = merchant;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const rewardRateLabel = t1;
  const channelLabel = CHANNEL_LABEL[merchant.channelType];
  let t2;
  if ($[2] !== merchant.merchantCode) {
    t2 = ROUTES.BENEFIT_MERCHANT_DETAIL.withSearchParams({
      dynamicPath: {
        merchantCode: merchant.merchantCode
      }
    });
    $[2] = merchant.merchantCode;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = cn("group -mx-8 flex items-center gap-16 rounded-xsmall px-8 py-4", INTERACTIVE_SURFACE);
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== merchant.logoImageUrl) {
    t4 = <Image src={merchant.logoImageUrl} alt="" width={40} height={40} className="size-40 shrink-0 rounded-pill border-1 border-Gray-100 bg-Gray-white object-contain p-4" />;
    $[5] = merchant.logoImageUrl;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== merchant.merchantName) {
    t5 = <p className="truncate font-600 text-Gray-black typo-body4">{merchant.merchantName}</p>;
    $[7] = merchant.merchantName;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== channelLabel || $[10] !== merchant.channelType) {
    t6 = channelLabel ? <Badge dimensions="H20" className={cn("shrink-0 px-4", CHANNEL_BADGE_CLASS_NAME[merchant.channelType])}>{channelLabel}</Badge> : null;
    $[9] = channelLabel;
    $[10] = merchant.channelType;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t5 || $[13] !== t6) {
    t7 = <div className="flex items-center gap-4">{t5}{t6}</div>;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== merchant.summary) {
    t8 = <p className="truncate font-600 text-Gray-600 typo-caption1">{merchant.summary}</p>;
    $[15] = merchant.summary;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7 || $[18] !== t8) {
    t9 = <div className="flex min-w-0 flex-1 flex-col gap-4">{t7}{t8}</div>;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== isPayoutBadgeVisible || $[21] !== merchant) {
    t10 = isPayoutBadgeVisible && isInstantPayout(merchant) ? <InstantPayoutBadge /> : null;
    $[20] = isPayoutBadgeVisible;
    $[21] = merchant;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== merchant.rewardCurrencyType || $[24] !== rewardRateLabel) {
    t11 = rewardRateLabel ? <span className="font-700 text-Gray-900 typo-caption1">{rewardRateLabel}{merchant.rewardCurrencyType === "AFFILIATE_PARTNER_POINT" ? " \uC81C\uD734\uD3EC\uC778\uD2B8" : null}</span> : null;
    $[23] = merchant.rewardCurrencyType;
    $[24] = rewardRateLabel;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <SvgChevronright className="size-16 shrink-0 text-Gray-400 transition group-hover:translate-x-1 group-hover:text-Gray-600 group-focus-visible:translate-x-1 group-focus-visible:text-Gray-600" />;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t11) {
    t13 = <span className="flex items-center gap-4">{t11}{t12}</span>;
    $[27] = t11;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t10 || $[30] !== t13) {
    t14 = <div className="flex shrink-0 flex-col items-end gap-4">{t10}{t13}</div>;
    $[29] = t10;
    $[30] = t13;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== t14 || $[33] !== t2 || $[34] !== t4 || $[35] !== t9) {
    t15 = <CustomLink href={t2} className={t3}>{t4}{t9}{t14}</CustomLink>;
    $[32] = t14;
    $[33] = t2;
    $[34] = t4;
    $[35] = t9;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  return t15;
};
export const MerchantBrandList = () => {
  const $ = _c(8);
  const {
    data: merchants
  } = useAffiliateMerchants();
  if (merchants.length === 0) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <MerchantListEmpty message={"\uC801\uB9BD \uAC00\uB2A5\uD55C \uC81C\uD734\uBAB0\uC774 \uC5C6\uC5B4\uC694."} />;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  let t0;
  if ($[1] !== merchants) {
    t0 = groupMerchantsBySection(merchants).map(_temp);
    $[1] = merchants;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] !== t0) {
    t1 = <div className="flex flex-col gap-4 pt-20 xl:gap-12">{t0}</div>;
    $[3] = t0;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <p className="px-20 pb-24 font-400 text-Gray-550 typo-caption1 xl:py-20 xl:pl-24">{AFFILIATE_DISCLOSURE}</p>;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t1) {
    t3 = <>{t1}{t2}</>;
    $[6] = t1;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
function _temp(t0, index) {
  const {
    sectionType,
    merchants: sectionMerchants
  } = t0;
  const {
    label,
    icon
  } = SECTION_HEADER[sectionType];
  const isSectionInstantPayout = sectionMerchants.every(isInstantPayout);
  return <section key={sectionType}>{index > 0 ? <div className="mb-4 h-10 bg-Gray-100 xl:hidden" /> : null}<div className="flex items-center gap-4 px-20 pt-16"><h2 className="flex items-center gap-4 font-700 text-Gray-900 typo-body3">{icon}{label}</h2>{isSectionInstantPayout ? <InstantPayoutBadge className="px-8 py-6" /> : null}</div><ul className="flex flex-col gap-12 p-20">{sectionMerchants.map(merchant => <li key={merchant.merchantCode}><MerchantRow merchant={merchant} isPayoutBadgeVisible={!isSectionInstantPayout} /></li>)}</ul></section>;
}